/**
 * 取引先検索（#288、docs/101 §4・Figma「継続案件と顧客コード（11/2）」①②③）。取引先を選ぶ口はこの部品1つ。
 *   PC は右から幅 420px のドロアー、スマホ（幅 767px 以下）は全画面のシート。入力中の画面は隠さない（幕は薄く）。
 *   上から 検索窓（窓の下に薄い字で「名称・フリガナ・コード・電話・担当者」）→ 何も打たなければ 最近（自分の5件）・よく使う（会社の10件）・フリガナ順の一覧と右端の「あ〜わ・未」の索引
 *   → 打つと候補（略称・正式名・コード・支店数・最終取引日。担当者が当たれば段下げで「担当者」の札＋名前＋支店）→ 足に「新規登録」（ドロアーの中で登録してそのまま選ぶ）。
 *   キー操作：↑↓ で動かし Enter で決定・候補が1件なら Enter で決定・Esc で閉じる・PC は「/」で検索窓へ。
 *   ローマ字（ibak）は画面がかなに直して kana で送る（contract の romajiToKana）。並びは API（DB の fn_partner_search）。
 * 役割：customer＝顧客と両方（見積書・納品書・請求書・{project}）／vendor＝仕入先と両方（{order}・見積依頼）
 * 選んだあとは PartnerChip（「アイバックス ／ 本社 ／ 射場 雅弘」＋「変更」）。説明の文はドロアーの中に置かない
 */
import {
  KANA_ROWS,
  PARTNER_CODE_MSG,
  PARTNER_MSG,
  PARTNER_PICKER_MSG as M,
  kanaRowOf,
  listNameOf,
  partnerCodeOf,
  romajiToKana,
  type KanaRow,
  type Partner,
  type PartnerRecent,
  type PartnerSearchHit,
  type PartnerSearchResult,
} from "@genbaryoku/contract";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ApiError, api, messageOf } from "../api.js";
import { t } from "../labels.js";
import { mdSlash } from "../lib/num.js";
import { useNarrow } from "../lib/useNarrow.js";
import { ErrorText } from "./ui.jsx";

export type PartnerPickRole = "customer" | "vendor";
/** 選んだ取引先（担当者・最近の行から選ぶと支店・担当者も） */
export type PickedPartner = {
  id: string;
  name: string;
  /** 略称（空は ""。#315：帳票の頭の「略称 ／ 支店 ／ 担当者」） */
  shortName?: string;
  displayName: string;
  honorific: string;
  branch: { id: string; name: string } | null;
  contact: { id: string; name: string } | null;
};

/** 会社らしい言葉が入っていれば法人（御中）。あとで取引先の画面で変えられる */
const guessKind = (name: string): "corporate" | "individual" =>
  /会社|法人|店|組合|事務所|工業|建設|商事|商会|工務店|電気|設備|工房|センター|デザイン|制作|印刷|システム|企画/.test(name) ? "corporate" : "individual";

/** 打った文字がコード・電話らしい（新規登録の名称に入れない） */
const codeLike = (q: string) => /^[0-9A-Za-z_\-() +]+$/.test(q.normalize("NFKC"));

const pickOf = (h: PartnerSearchHit, extra?: Partial<Pick<PickedPartner, "branch" | "contact">>): PickedPartner => ({
  id: h.id,
  name: h.name,
  shortName: h.shortName,
  displayName: h.displayName,
  honorific: h.honorific,
  branch: extra?.branch ?? null,
  contact: extra?.contact ?? null,
});

type Opt = { key: string; pick: PickedPartner };

/** 候補の行（取引先）の2行目：正式名・コード・支店数・最終取引日 */
function subOf(h: PartnerSearchHit, role: PartnerPickRole): string {
  const code = role === "vendor" ? h.vendorCode || h.customerCode : h.customerCode || h.vendorCode;
  return [h.name !== listNameOf(h) ? h.name : "", code, h.branchCount ? M.branchCount(h.branchCount) : "", h.lastDealOn ? M.lastDeal(mdSlash(h.lastDealOn)) : ""].filter(Boolean).join(" ・ ");
}

export function PartnerPickerDrawer({
  open,
  role,
  onClose,
  onPick,
  initialQuery = "",
}: {
  open: boolean;
  role: PartnerPickRole;
  onClose: () => void;
  onPick: (p: PickedPartner) => void;
  /** 開いたときの検索の文字（欄で打ち始めた1文字など） */
  initialQuery?: string;
}) {
  if (!open) return null;
  return createPortal(<DrawerBody role={role} onClose={onClose} onPick={onPick} initialQuery={initialQuery} />, document.body);
}

function DrawerBody({ role, onClose, onPick, initialQuery }: { role: PartnerPickRole; onClose: () => void; onPick: (p: PickedPartner) => void; initialQuery: string }) {
  const narrow = useNarrow();
  const ids = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(typeof document !== "undefined" ? document.activeElement : null);
  const [q, setQ] = useState(initialQuery);
  const [debounced, setDebounced] = useState(initialQuery.trim());
  const [active, setActive] = useState(-1);
  const [registering, setRegistering] = useState(false);
  const typed = debounced.trim();
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 120);
    return () => clearTimeout(h);
  }, [q]);
  useEffect(() => setActive(-1), [typed]);
  useEffect(() => {
    inputRef.current?.focus();
    const el = opener.current;
    return () => {
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, []);

  const kana = typed ? romajiToKana(typed) : null;
  const search = useQuery({
    queryKey: ["partner-search", role, typed, kana],
    queryFn: () => api<PartnerSearchResult>("GET", `/partners/search?role=${role}&q=${encodeURIComponent(typed)}${kana ? `&kana=${encodeURIComponent(kana)}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const recent = useQuery({ queryKey: ["partner-recent", role], queryFn: () => api<PartnerRecent>("GET", `/partners/recent?role=${role}`), enabled: !typed });
  /** いま打った文字の答えが届いている（前の答えを見せているあいだは Enter を効かせない） */
  const ready = search.isSuccess && !search.isPlaceholderData && q.trim() === typed;
  const hits = typed ? (search.data?.items ?? []) : [];
  /** 何も打たないときは 最近・よく使う と フリガナ順の一覧 がそろってから一度に描く（#179：先に一覧を出すと、あとから入る最近に押し下げられる） */
  const listReady = !typed && ready && !!recent.data;
  const list = listReady ? (search.data?.items ?? []) : [];

  // 何も打たないときの一覧：フリガナの行ごと（未設定は最後）
  const groups = useMemo(() => {
    const m = new Map<KanaRow, PartnerSearchHit[]>();
    for (const h of list) {
      const r = kanaRowOf(h.nameKana);
      m.set(r, [...(m.get(r) ?? []), h]);
    }
    return [...KANA_ROWS, "未" as const].filter((r) => m.has(r)).map((r) => ({ row: r, items: m.get(r)! }));
  }, [list]);

  // キー操作の並び（見えている順）
  const opts: Opt[] = useMemo(() => {
    if (typed) return hits.flatMap((h) => [{ key: `h:${h.id}`, pick: pickOf(h) }, ...h.contacts.map((c) => ({ key: `c:${c.id}`, pick: pickOf(h, { branch: c.branchId ? { id: c.branchId, name: c.branchName } : null, contact: { id: c.id, name: c.name } }) }))]);
    const r = recent.data?.recent ?? [];
    const f = recent.data?.frequent ?? [];
    return [
      ...r.map((h) => ({ key: `r:${h.id}`, pick: pickOf(h, { branch: h.branch, contact: h.contact }) })),
      ...f.map((h) => ({ key: `f:${h.id}`, pick: pickOf(h) })),
      ...groups.flatMap((g) => g.items.map((h) => ({ key: `l:${h.id}`, pick: pickOf(h) }))),
    ];
  }, [typed, hits, recent.data, groups]);

  const choose = (p: PickedPartner) => {
    onPick(p);
    onClose();
  };
  /** 候補が届く前に押した Enter（届いて1件なら、その候補で決める。#179 の前の選び方と同じ） */
  const enterWanted = useRef(false);
  useEffect(() => {
    enterWanted.current = false;
  }, [q]);
  useEffect(() => {
    if (!enterWanted.current || !ready) return;
    enterWanted.current = false;
    if (typed && hits.length === 1) choose(pickOf(hits[0]!));
  });
  const onKey = (e: React.KeyboardEvent) => {
    // ドロアーは portal でも React の木では呼んだ画面の子＝窓（見積依頼の作成など）の Esc・Enter に届かないように止める
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      if (registering) setRegistering(false);
      else onClose();
      return;
    }
    if (registering) return;
    const inInput = e.target === inputRef.current;
    if (e.key === "/" && !inInput && !narrow) {
      e.preventDefault();
      inputRef.current?.focus();
      return;
    }
    if (!inInput) return;
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!opts.length) return;
      const n = e.key === "ArrowDown" ? (active + 1) % opts.length : active <= 0 ? opts.length - 1 : active - 1;
      setActive(n);
      listRef.current?.querySelector(`[data-opt="${CSS.escape(opts[n]!.key)}"]`)?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && opts[active]) choose(opts[active]!.pick);
      else if (typed && ready && hits.length === 1) choose(pickOf(hits[0]!));
      else if (q.trim() && !ready) enterWanted.current = true;
    }
  };
  const activeKey = active >= 0 ? opts[active]?.key : undefined;
  const optProps = (key: string, pick: PickedPartner) => ({
    role: "option" as const,
    "aria-selected": key === activeKey,
    "data-opt": key,
    id: `${ids}-${key}`,
    className: "ppd-opt" + (key === activeKey ? " is-active" : ""),
    onClick: () => choose(pick),
    onMouseMove: () => {
      const i = opts.findIndex((o) => o.key === key);
      if (i !== active) setActive(i);
    },
  });

  const title = M.title;
  const count = typed && search.data && !search.isPlaceholderData ? M.count(hits.length) : "";
  const jump = (r: KanaRow) => listRef.current?.querySelector(`[data-row="${r}"]`)?.scrollIntoView({ block: "start" });

  const partnerRow = (h: PartnerSearchHit, key: string, right?: ReactNode, label?: string) => (
    <div key={key} {...optProps(key, key.startsWith("r:") ? pickOf(h, { branch: h.branch, contact: h.contact }) : pickOf(h))} data-testid="partner-hit">
      <span className="ppd-main">
        <b className="ppd-name">{label ?? listNameOf(h)}</b>
        <span className="ppd-sub">{subOf(h, role)}</span>
      </span>
      {right ? <span className="ppd-right">{right}</span> : null}
      <Chevron />
    </div>
  );

  return (
    <>
      <div
        className="ppd-dim"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-hidden="true"
      />
      <aside className={"ppd" + (narrow ? " is-full" : "")} role="dialog" aria-modal="true" aria-label={title} onKeyDown={onKey} onClick={(e) => e.stopPropagation()} onSubmit={(e) => e.stopPropagation()} data-testid="partner-drawer" data-notice-above="">
        <header className="ppd-head">
          {narrow ? (
            <button type="button" className="btn text ppd-cancel" onClick={() => (registering ? setRegistering(false) : onClose())}>
              {M.cancel}
            </button>
          ) : null}
          <h2 className="ppd-title">{registering ? M.newPartner : title}</h2>
          {!narrow ? (
            <button type="button" className="ppd-x" aria-label={M.close} title={M.close} onClick={onClose} data-testid="partner-drawer-close">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
            </button>
          ) : null}
        </header>
        {registering ? (
          <RegisterForm role={role} initialName={codeLike(q) ? "" : q.trim()} initialCode={codeLike(q) ? partnerCodeOf(q) : ""} onDone={(p) => choose(p)} onCancel={() => setRegistering(false)} />
        ) : (
          <>
            <div className="ppd-search">
              <span className="ppd-sbox">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="ppd-sicon">
                  <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" fill="none" />
                  <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  ref={inputRef}
                  type="search"
                  className="ppd-input"
                  aria-label={title}
                  aria-describedby={`${ids}-hint`}
                  aria-controls={`${ids}-list`}
                  aria-activedescendant={activeKey ? `${ids}-${activeKey}` : undefined}
                  autoComplete="off"
                  enterKeyHint="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="partner-search"
                />
              </span>
              <div className="ppd-meta">
                <span data-testid="partner-count">{count}</span>
                <span id={`${ids}-hint`} className="ppd-hint">{M.hint}</span>
              </div>
            </div>
            <div className="ppd-body">
              <div className="ppd-list" ref={listRef} id={`${ids}-list`} role="listbox" aria-label={title}>
                {search.isError ? <ErrorText>{messageOf(search.error)}</ErrorText> : null}
                {typed ? (
                  hits.length ? (
                    hits.map((h) => (
                      <div key={h.id} className="ppd-grp">
                        {partnerRow(h, `h:${h.id}`)}
                        {h.contacts.map((c) => (
                          <div key={c.id} {...optProps(`c:${c.id}`, pickOf(h, { branch: c.branchId ? { id: c.branchId, name: c.branchName } : null, contact: { id: c.id, name: c.name } }))} data-testid="partner-contact-hit">
                            <span className="ppd-main ppd-crow">
                              <span className="ppd-tag">{M.contact}</span>
                              <b className="ppd-cname">{c.name}</b>
                              {c.branchName ? <span className="ppd-sub">／ {c.branchName}</span> : null}
                            </span>
                            <Chevron />
                          </div>
                        ))}
                      </div>
                    ))
                  ) : ready ? (
                    <p className="ppd-empty" data-testid="partner-none">{M.none}</p>
                  ) : null
                ) : (
                  <>
                    {listReady && recent.data?.recent.length ? (
                      <section aria-label={M.recent}>
                        <h3 className="ppd-sec">{M.recent}</h3>
                        {recent.data.recent.map((h) =>
                          partnerRow(h, `r:${h.id}`, h.lastDealOn ? mdSlash(h.lastDealOn) : null, [listNameOf(h), h.branch?.name, h.contact?.name].filter(Boolean).join(" ／ ")),
                        )}
                      </section>
                    ) : null}
                    {listReady && recent.data?.frequent.length ? (
                      <section aria-label={M.frequent}>
                        <h3 className="ppd-sec">{M.frequent}</h3>
                        {recent.data.frequent.map((h) => partnerRow(h, `f:${h.id}`, <span title={M.docCountTitle}>{M.docCount(h.docCount ?? 0)}</span>))}
                      </section>
                    ) : null}
                    {groups.map((g) => (
                      <section key={g.row} aria-label={g.row === "未" ? M.unset : g.row}>
                        <h3 className="ppd-sec" data-row={g.row}>
                          {g.row === "未" ? M.unset : g.row}
                        </h3>
                        {g.items.map((h) => partnerRow(h, `l:${h.id}`))}
                      </section>
                    ))}
                    {listReady && !list.length ? <p className="ppd-empty">{t(M.noPartner)}</p> : null}
                  </>
                )}
              </div>
              {!typed && groups.length ? (
                <nav className="ppd-index" aria-label="索引">
                  {[...KANA_ROWS, "未" as const].map((r) => {
                    const has = groups.some((g) => g.row === r);
                    return (
                      <button key={r} type="button" disabled={!has} aria-label={r === "未" ? M.unset : r} onClick={() => jump(r)} data-testid={`partner-index-${r}`}>
                        {r === "未" ? M.unsetShort : r}
                      </button>
                    );
                  })}
                </nav>
              ) : null}
            </div>
            <footer className="ppd-foot">
              <button type="button" className="btn ppd-new" onClick={() => setRegistering(true)} data-testid="partner-new">
                {M.newPartner}
              </button>
            </footer>
          </>
        )}
        <span className="sr" aria-live="polite">{count}</span>
      </aside>
    </>
  );
}

function Chevron() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="ppd-chev">
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

/** ドロアーの中の新規登録（名称・フリガナ・顧客コード／仕入先コード）。登録したらそのまま選ぶ */
function RegisterForm({ role, initialName, initialCode, onDone, onCancel }: { role: PartnerPickRole; initialName: string; initialCode: string; onDone: (p: PickedPartner) => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const ids = useId();
  const [name, setName] = useState(initialName);
  const [kana, setKana] = useState("");
  const [code, setCode] = useState(initialCode);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ name?: string | undefined; code?: string | undefined; all?: string | undefined }>({});
  const codeKey = role === "vendor" ? "vendorCode" : "customerCode";
  const codeLabel = role === "vendor" ? M.vendorCode : M.customerCode;
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => nameRef.current?.focus(), []);
  const submit = async () => {
    const n = name.trim();
    if (!n) {
      setErr({ name: t(PARTNER_MSG.nameRequired) });
      return;
    }
    const c = partnerCodeOf(code);
    if (c && !/^[A-Za-z0-9_-]{1,20}$/.test(c)) {
      setErr({ code: PARTNER_CODE_MSG.shape });
      return;
    }
    setBusy(true);
    setErr({});
    try {
      const p = await api<Partner>("POST", "/partners", { name: n, nameKana: kana.trim() || undefined, kind: guessKind(n), partnerType: role === "vendor" ? "vendor" : "client", [codeKey]: c || undefined });
      void qc.invalidateQueries({ queryKey: ["partners"] });
      void qc.invalidateQueries({ queryKey: ["partner-search"] });
      onDone({ id: p.id, name: p.name, shortName: p.shortName, displayName: p.displayName, honorific: p.honorific, branch: null, contact: null });
    } catch (e) {
      const errors = e instanceof ApiError ? ((e.problem as { errors?: Record<string, string> }).errors ?? {}) : {};
      setErr({ name: errors.name, code: errors[codeKey], all: errors.name || errors[codeKey] ? undefined : messageOf(e) });
      setBusy(false);
    }
  };
  return (
    <form
      className="ppd-reg"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      data-testid="partner-register"
    >
      <div className="ppd-regbody">
        <div className="field">
          <input ref={nameRef} id={`${ids}-n`} className="inp" value={name} maxLength={200} placeholder=" " onChange={(e) => setName(e.target.value)} aria-invalid={err.name ? true : undefined} data-testid="partner-register-name" />
          <label htmlFor={`${ids}-n`}>{M.name}</label>
        </div>
        <ErrorText>{err.name}</ErrorText>
        <div className="field">
          <input id={`${ids}-k`} className="inp" value={kana} maxLength={100} placeholder=" " onChange={(e) => setKana(e.target.value)} data-testid="partner-register-kana" />
          <label htmlFor={`${ids}-k`}>{M.kana}</label>
        </div>
        <div className="field">
          <input id={`${ids}-c`} className="inp code" value={code} maxLength={40} placeholder=" " inputMode="text" autoCapitalize="characters" onChange={(e) => setCode(e.target.value)} onBlur={() => setCode((v) => partnerCodeOf(v))} aria-invalid={err.code ? true : undefined} data-testid="partner-register-code" />
          <label htmlFor={`${ids}-c`}>{codeLabel}</label>
        </div>
        <ErrorText>{err.code}</ErrorText>
        <ErrorText>{err.all}</ErrorText>
      </div>
      <footer className="ppd-foot ppd-regfoot">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {M.cancel}
        </button>
        <button type="submit" className="btn primary" disabled={busy || !name.trim()} data-testid="partner-register-save">
          {M.register}
        </button>
      </footer>
    </form>
  );
}

/**
 * 取引先の欄（作成画面・帳票の頭）：選ぶ前は押すとドロアーが開く欄（英数字を打ち始めてもその字で開く）、選んだあとは1行のチップ＋「変更」。
 * value は表示だけ（選んだ取引先・支店・担当者の名前）
 */
export function PartnerField({
  role,
  label,
  value,
  onPick,
  autoFocus,
  testId = "partner-field",
  disabled,
}: {
  role: PartnerPickRole;
  /** 欄の名前（{customer}・{vendor} を埋めた言葉） */
  label: string;
  value: { name: string; branch?: string | null; contact?: string | null } | null;
  onPick: (p: PickedPartner) => void;
  autoFocus?: boolean;
  testId?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState("");
  const show = (s = "") => {
    setFirst(s);
    setOpen(true);
  };
  return (
    <>
      {value ? (
        <PartnerChip label={label} name={value.name} branch={value.branch ?? null} contact={value.contact ?? null} onChange={disabled ? undefined : () => show()} testId={`${testId}-chip`} changeTestId={`${testId}-change`} />
      ) : (
        <button
          type="button"
          className="ppd-field"
          autoFocus={autoFocus}
          disabled={disabled}
          onClick={() => show()}
          onKeyDown={(e) => {
            if (e.key.length === 1 && /[0-9A-Za-z]/.test(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey) {
              e.preventDefault();
              show(e.key);
            }
          }}
          data-testid={testId}
        >
          <span className="ppd-fieldlbl">{label}</span>
          <span className="ppd-fieldph">{M.title}</span>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="ppd-sicon">
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" fill="none" />
            <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
      <PartnerPickerDrawer open={open} role={role} initialQuery={first} onClose={() => setOpen(false)} onPick={onPick} />
    </>
  );
}

/** 選んだ取引先の1行（Figma ③）：「アイバックス ／ 本社 ／ 射場 雅弘」＋「変更」 */
export function PartnerChip({
  label,
  name,
  branch,
  contact,
  onChange,
  testId = "picked-client",
  changeTestId = "client-change",
}: {
  label: string;
  name: string;
  branch: string | null;
  contact: string | null;
  onChange?: (() => void) | undefined;
  testId?: string;
  changeTestId?: string;
}) {
  return (
    <div className="ppd-chip" data-testid={testId}>
      <span className="ppd-chiplbl">{label}</span>
      <span className="ppd-chiptext">
        <b>{name}</b>
        {branch ? <span> ／ {branch}</span> : null}
        {contact ? <span> ／ {contact}</span> : null}
      </span>
      {onChange ? (
        <button type="button" className="btn text sm" onClick={onChange} data-testid={changeTestId}>
          {M.change}
        </button>
      ) : null}
    </div>
  );
}
